/**
 * Anneau de collines autour de la carte jouable : prolonge le relief entre
 * le bord de la carte et le fond de montagnes (2,8 km au-delà du bord),
 * pour qu'aucun vide n'apparaisse en vue plan. Très peu de triangles (pas de
 * 256 m), forêt enneigée en couleurs de sommet, aucune interaction.
 */
import * as THREE from 'three/webgpu';
import { createNoise2D, fbm, smoothstep, type Heightfield } from '@snowchain/shared';

/**
 * `relief` : fonction de relief de la carte, quand elle se prolonge
 * naturellement au-delà du bord (cartes « massif ») : les versants y
 * continuent au lieu d'une plate-forme à la hauteur du bord.
 */
export function createApron(hf: Heightfield, relief?: (x: number, z: number) => number): THREE.Mesh {
  const step = 256;
  const half = hf.half;
  // L'anneau s'étend à 2 816 m au-delà du bord (3 840 m de rayon sur Val Cendrée).
  const R = Math.max(15 * step, Math.ceil((half + 2816) / step) * step);
  const n = (2 * R) / step + 1;
  const noise = createNoise2D(913);
  const pos = new Float32Array(n * n * 3);
  const col = new Float32Array(n * n * 3);
  const forest = new THREE.Color(0x3d5a47);
  const snow = new THREE.Color(0xe6edf2);
  const rock = new THREE.Color(0x7c8b95);
  const c = new THREE.Color();
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const x = -R + i * step;
      const z = -R + j * step;
      const inside = Math.abs(x) <= half && Math.abs(z) <= half;
      const cx = Math.max(-half, Math.min(half, x));
      const cz = Math.max(-half, Math.min(half, z));
      const edge = hf.height(cx, cz);
      const out = Math.max(Math.abs(x) - half, Math.abs(z) - half, 0);
      const nz = fbm(noise, x / 900, z / 900, 3);
      let h: number;
      if (inside) h = edge;
      else if (relief) {
        // Relief prolongé, puis des collines qui ferment l'horizon.
        h = relief(x, z) + smoothstep(0, 2600, out) * (260 + 200 * nz);
      } else {
        // Les collines montent vers le fond de montagnes, plus haut au nord.
        const north = smoothstep(800, -2500, z);
        h = edge + smoothstep(0, 2600, out) * (220 + 380 * north + 160 * nz);
      }
      const k = (j * n + i) * 3;
      pos[k] = x;
      pos[k + 1] = h;
      pos[k + 2] = z;
      // Forêt enneigée en bas, neige et roche plus haut. Sur une carte massif, la carte elle-même
      // est blanche piquée de sapins : la teinte de forêt ne se renforce qu'en s'éloignant du bord.
      let tree = smoothstep(650, 380, h) * (0.55 + 0.45 * nz);
      if (relief) tree *= 0.15 + 0.5 * smoothstep(0, 2200, out);
      c.copy(snow).lerp(forest, Math.max(0, Math.min(0.85, tree)));
      if (h > 700) c.lerp(rock, Math.min(0.5, (h - 700) / 600) * (0.5 + 0.5 * nz));
      col[k] = c.r;
      col[k + 1] = c.g;
      col[k + 2] = c.b;
    }
  }
  const idx: number[] = [];
  const inMap = (i: number, j: number) => Math.abs(-R + i * step) <= half && Math.abs(-R + j * step) <= half;
  for (let j = 0; j < n - 1; j++) {
    for (let i = 0; i < n - 1; i++) {
      if (inMap(i, j) && inMap(i + 1, j) && inMap(i, j + 1) && inMap(i + 1, j + 1)) continue;
      const a = j * n + i;
      idx.push(a, a + n, a + 1, a + 1, a + n, a + n + 1);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  const m = new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.95, metalness: 0 });
  const mesh = new THREE.Mesh(g, m);
  mesh.name = 'collines';
  mesh.receiveShadow = false;
  mesh.frustumCulled = false;
  return mesh;
}
